'use client';
import React from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FacebookIcon, InstagramIcon, LinkedinIcon, YoutubeIcon } from 'lucide-react';
import sctLogo from '../../assets/SCT.png';

interface FooterLink {
	title: string;
	href: string;
	icon?: React.ComponentType<{ className?: string }>;
}

interface FooterSection {
	label: string;
	links: FooterLink[];
}

const footerLinks: FooterSection[] = [
	{
		label: 'Product',
		links: [
			{ title: 'Pricing Plan', href: 'https://smilect.support' },
			{ title: 'Network Scanner', href: '/scanner' },
			{ title: 'Our Services', href: '/our-services' },
			{ title: 'Internships', href: '/internship' },
		],
	},
	{
		label: 'Company',
		links: [
			{ title: 'Meet Our Team', href: '/team' },
			{ title: 'Our Story', href: '/our-story' },
			{ title: 'Contact Us', href: '/contact' },
			{ title: 'Blogs', href: '/blogs' },
		],
	},
	{
		label: 'Legal',
		links: [
			{ title: 'Privacy Policy', href: '/privacy-policy' },
			{ title: 'Terms of Service', href: '/terms' },
			{ title: 'Accessibility', href: '#' },
			{ title: 'Help & FAQ', href: '/contact#faq' },
		],
	},
	{
		label: 'Social Links',
		links: [
			{ title: 'Facebook', href: '#', icon: FacebookIcon },
			{ title: 'Instagram', href: '#', icon: InstagramIcon },
			{ title: 'Youtube', href: '#', icon: YoutubeIcon },
			{ title: 'LinkedIn', href: '#', icon: LinkedinIcon },
		],
	},
];

export function Footer() {
	return (
		<motion.footer 
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-50px" }}
			transition={{ duration: 0.8, ease: "easeOut" }}
			className="relative w-full flex flex-col items-center justify-center rounded-t-[2rem] border-t border-white/10 bg-[#0B1121] pt-10 pb-8 z-20"
		>
			<div className="w-full max-w-7xl mx-auto px-6 md:px-12 relative">
			<div className="bg-white/20 absolute top-0 right-1/2 left-1/2 h-px w-1/3 -translate-x-1/2 -translate-y-1/2 rounded-full blur" />

			<div className="grid w-full gap-8 xl:grid-cols-3 xl:gap-8">
				<AnimatedContainer className="space-y-4">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
							<img src={sctLogo} alt="SCT" className="w-full h-full object-cover" />
						</div>
						<span className="text-xl font-bold text-white tracking-wide">Smile Cyber Tech</span>
					</div>
					<p className="text-slate-400 mt-8 text-sm md:mt-0 leading-relaxed max-w-xs">
						Demystifying cybersecurity to build a safer, approachable digital environment for all.
					</p>
					<p className="text-slate-500 mt-8 text-sm md:mt-0 pt-4">
						© {new Date().getFullYear()} Smile Cyber Tech. All rights reserved.
					</p>
				</AnimatedContainer>

				<div className="mt-10 grid grid-cols-2 gap-8 md:grid-cols-4 xl:col-span-2 xl:mt-0">
					{footerLinks.map((section, index) => (
						<AnimatedContainer key={section.label} delay={0.1 + index * 0.1}>
							<div className="mb-10 md:mb-0">
								<h3 className="text-xs font-semibold uppercase tracking-wider text-white mb-4">{section.label}</h3>
								<ul className="text-slate-400 mt-4 space-y-3 text-sm">
									{section.links.map((link) => (
										<li key={link.title}>
											<a
												href={link.href}
												className="hover:text-white inline-flex items-center transition-all duration-300"
											>
												{link.icon && <link.icon className="me-2 size-4" />}
												{link.title}
											</a>
										</li>
									))}
								</ul>
							</div>
						</AnimatedContainer>
					))}
				</div>
				</div>
			</div>
		</motion.footer>
	);
};

type ViewAnimationProps = {
	delay?: number;
	className?: ComponentProps<typeof motion.div>['className'];
	children: ReactNode;
};

function AnimatedContainer({ className, delay = 0.1, children }: ViewAnimationProps) {
	const shouldReduceMotion = useReducedMotion();

	if (shouldReduceMotion) {
		return children;
	}

	return (
		<motion.div
			initial={{ filter: 'blur(4px)', translateY: -8, opacity: 0 }}
			whileInView={{ filter: 'blur(0px)', translateY: 0, opacity: 1 }}
			viewport={{ once: true }}
			transition={{ delay, duration: 0.8 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};
